/** @jsx jsx */

import { Fragment, ReactNode } from 'react';

import { css, jsx } from '@emotion/core';
import {
  Colors,
  Icon,
  Spacer,
  Text,
  TooltipWrapper,
} from '@zapier/design-system';
import { isNumber } from 'lodash';

import LinkWithArrow from 'app/developer-v3/components/LinkWithArrow';

const styleRoot = css`
  padding: 20px;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  border: 1px solid ${Colors.GrayWarm4};
`;

const styleTitle = css`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const styleTooltipWrapper = css`
  min-width: 250px;
  text-align: center;
`;

const styleFigure = css`
  display: flex;
  height: 130px;
  margin-bottom: 15px;
`;

const styleFigureGraph = ({ isBeta }) => css`
  flex: 0 0;
  margin-right: 30px;
  min-width: ${isBeta ? '120px;' : 'auto'};
  text-align: center;
`;

const styleFigureStats = css`
  flex: 1;
`;

const styleFigureStatsCurrent = css`
  display: inline-flex;
`;

const styleFigureStatsLines = css`
  padding-top: 10px;
`;

const styleFigureStatsText = css`
  margin-bottom: 10px;
`;

const styleFlexAuto = css`
  flex: auto;
`;

function HelpTip({ children }) {
  return (
    <TooltipWrapper
      allowMultilineTooltip={true}
      content={<div css={styleTooltipWrapper}>{children}</div>}
      isBlock={true}
    >
      {() => (
        <Icon
          aria-label="Help"
          color="GrayWarm6"
          name="alertInfoFill"
          size={18}
        />
      )}
    </TooltipWrapper>
  );
}

function StarTip({ children }) {
  return (
    <TooltipWrapper
      allowMultilineTooltip={true}
      content={<div css={styleTooltipWrapper}>{children}</div>}
      isBlock={true}
    >
      {() => (
        <Icon color="StatusSuccessWeaker" name="ratingStarFill" size={20} />
      )}
    </TooltipWrapper>
  );
}

export function StatTextLine({ children }: { children: ReactNode }) {
  return <div css={styleFigureStatsText}>{children}</div>;
}

export function StatTextValue({ children }: { children: number | string }) {
  return (
    <Text color="GrayWarm7" type="Body4">
      {!isNumber(children) ? children.toLocaleString() : children}
    </Text>
  );
}

type Props = {
  children: ReactNode;
  className?: string;
  cta?: {
    link: string;
    text: string;
    useNewTab?: boolean;
  };
  figure: ReactNode;
  help?: string;
  isBeta: boolean;
  stats: {
    completedTip?: ReactNode;
    current?: JSX.Element | string | number;
    lines: ReactNode;
    title: string;
  };
  title: string;
};

export default function Module({
  children,
  cta,
  figure,
  help,
  isBeta,
  stats,
  title,
}: Props) {
  return (
    <div css={styleRoot}>
      <div css={styleTitle}>
        <Text margin="0px 5px 0px 0px" type="Body4">
          {title}
        </Text>
        {!!help && <HelpTip>{help}</HelpTip>}
      </div>
      <div css={styleFigure}>
        <div css={styleFigureGraph({ isBeta })}>{figure}</div>
        <div aria-label={stats.title} css={styleFigureStats}>
          {stats.title && <Text tag="div">{stats.title}</Text>}
          <div css={styleFigureStatsCurrent} id={stats.title}>
            <Text margin="0px 5px 0px 0px" type="ParagraphHeader4">
              {stats.current}
            </Text>
            {!!stats.completedTip && <StarTip>{stats.completedTip}</StarTip>}
          </div>
        </div>
      </div>
      <div css={styleFigureStatsLines}>
        <Text>{stats.lines}</Text>
      </div>
      {isBeta && <div css={styleFlexAuto} />}
      <div>{children}</div>
      {!!cta && (
        <Fragment>
          <Spacer height={20} />
          <LinkWithArrow
            href={cta.link}
            target={cta.useNewTab ? '_blank' : undefined}
          >
            {cta.text}
          </LinkWithArrow>
        </Fragment>
      )}
    </div>
  );
}
